<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useTemplateRefsList } from '@vueuse/core';
import type {
  ActionType,
  ApplicantIncome,
  Employment,
  EmploymentEntry,
  EmploymentTypeEnum,
  RetirementDetails,
} from '@/types';
import { EmploymentType } from '@/helpers/const';
import { defaultAddress, defaultApplicantIncome, defaultPeriodicValue } from '@/helpers/builder';
import { PlusCircleIcon } from '@heroicons/vue/20/solid';
import EmploymentCard from './EmploymentCard.vue';
import AdditionalIncomeForm from './AdditionalIncomeForm.vue';
import RetirementDetailsForm from './RetirementDetailsForm.vue';

const props = withDefaults(
  defineProps<{
    applicantId: string;
    modelValue: ApplicantIncome | null;
    retirementDetails?: RetirementDetails | null;
    showButtons?: boolean;
    animate?: boolean;
    /** Declaration flow passes the loaded application's action so salary validation stays on that application. */
    actionType?: ActionType;
  }>(),
  {
    retirementDetails: null,
    showButtons: false,
    animate: true,
    actionType: undefined,
  },
);

const emit = defineEmits<{
  (e: 'update:modelValue', v: ApplicantIncome): void;
  (e: 'update:retirementDetails', v: RetirementDetails): void;
  (e: 'save'): void;
  (e: 'cancel'): void;
}>();

const applicantIncome = ref<ApplicantIncome | null>(props.modelValue);
const employmentList = ref<{ item: EmploymentEntry }[]>(
  (applicantIncome.value?.employment ?? []).map((item, index) => {
    return { item: { ...item, elementId: `${props.applicantId}-employment-${index}` } };
  }),
);
const employmentRefs = useTemplateRefsList<InstanceType<typeof EmploymentCard>>();
const additionalIncomeRef = ref<InstanceType<typeof AdditionalIncomeForm>>();

const retirementDetailsRef = ref<InstanceType<typeof RetirementDetailsForm>>();

const primaryEmploymentType = ref<EmploymentTypeEnum | undefined>(applicantIncome.value?.employment?.[0]?.type);

/** Whether the applicant's first employment source is "Retired". */
const isApplicantRetired = computed(() => primaryEmploymentType.value === EmploymentType.RETIRED);

const validateEmployment = async () => {
  let isValid = true;
  for (const itemRef of employmentRefs.value) {
    const isEmploymentDetailValid = await itemRef.update();
    isValid = isValid && !!isEmploymentDetailValid;
    if (!isEmploymentDetailValid) {
      itemRef.expandAndFocus();
    }
  }
  return isValid;
};

const addEmployment = async () => {
  if (await validateEmployment()) {
    const elementId = `${props.applicantId}-employment-${employmentList.value.length}`;
    employmentList.value.push({ item: { elementId } });
    setTimeout(() => {
      const lastItem = employmentRefs.value.length - 1;
      employmentRefs.value[lastItem].$el.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'start' });
    }, 320);
  }
};

onMounted(() => {
  if (employmentList.value.length === 0) {
    addEmployment();
  }
});

const removeEmployment = (elementId: string) => {
  employmentList.value = employmentList.value.filter((employment) => employment.item.elementId !== elementId);
};

const update = async () => {
  const isValidEmployment = await validateEmployment();
  const isValidIncome = (await additionalIncomeRef.value?.update()) ?? false;
  const isValidRetirement = (await retirementDetailsRef.value?.update()) ?? false;
  const isValid = isValidEmployment && isValidIncome && isValidRetirement;

  if (isValid) {
    const employment: Employment[] = employmentList.value.map(({ item }) =>
      item.type === EmploymentType.FULL_TIME ||
      item.type === EmploymentType.PART_TIME ||
      item.type === EmploymentType.CASUAL
        ? {
            id: item.id || undefined,
            type: item.type ?? EmploymentType.FULL_TIME,
            salary: item.salary ?? defaultPeriodicValue(),
            commission: item.commission ?? defaultPeriodicValue(),
            bonuses: item.bonuses ?? defaultPeriodicValue(),
            overtime: item.overtime ?? defaultPeriodicValue(),
            salarySacrifice: item.salarySacrifice ?? false,
            employerName: item.employerName ?? '',
            employerAddress: item.employerAddress ?? defaultAddress(),
            occupation: item.occupation ?? '',
            industry: item.industry ?? '',
            employedSince: item.employedSince ?? undefined,
          }
        : item.type === EmploymentType.SELF_EMPLOYED
        ? {
            id: item.id || undefined,
            type: EmploymentType.SELF_EMPLOYED,
            salary: item.salary ?? defaultPeriodicValue(),
            businessAddress: item.businessAddress ?? defaultAddress(),
            businessType: item.businessType ?? '',
            tradingName: item.tradingName ?? '',
            occupation: item.occupation ?? '',
            industry: item.industry ?? '',
            gstRegistered: item.gstRegistered ?? false,
            abn: item.abn ?? '',
            soleTrader: item.soleTrader ?? false,
            hasTaxReturns: item.hasTaxReturns ?? false,
            employedSince: item.employedSince ?? undefined,
            providedDocument: item.providedDocument,
            otherProvidedDocument: item.otherProvidedDocument,
          }
        : {
            id: item.id || undefined,
            type: item.type ?? EmploymentType.UNEMPLOYED,
          },
    );

    const income = Object.assign(defaultApplicantIncome(), applicantIncome.value, { employment });
    emit('update:modelValue', income);
  }
  return isValid;
};

const save = async () => {
  if (await update()) {
    emit('save');
  }
};

const cancel = () => {
  employmentRefs.value.forEach((ref) => {
    ref.cancel();
  });
  additionalIncomeRef.value?.cancel();
  retirementDetailsRef.value?.cancel();
  emit('cancel');
};

/** Merges retired income into applicantIncome, skipping undefined fields so existing values are not wiped. */
const mergeRetiredIncome = (v: Partial<ApplicantIncome>) => {
  const defined = Object.fromEntries(Object.entries(v).filter(([, value]) => value !== undefined));
  if (applicantIncome.value) Object.assign(applicantIncome.value, defined);
  else applicantIncome.value = { ...defaultApplicantIncome(), ...defined };
};

const showAnotherButton = ref(true);

/** Updates UI state when the primary employment type changes. */
/** Updates UI state when the primary (index 0) employment type changes. */
const showAdditionBtn = (type: EmploymentTypeEnum, cardIndex: number) => {
  if (cardIndex !== 0) return;
  primaryEmploymentType.value = type;
  showAnotherButton.value = type !== EmploymentType.RETIRED && type !== EmploymentType.UNEMPLOYED;
};

defineExpose({ update });
</script>

<template>
  <div>
    <template v-for="(job, index) in employmentList" :key="job.item.elementId">
      <EmploymentCard
        :ref="employmentRefs.set"
        v-model="job.item"
        :applicant-id="job.item.elementId || ''"
        :is-multiple="employmentList.length > 1"
        :card-index="index"
        :action-type="props.actionType"
        :retired-income-data="isApplicantRetired ? applicantIncome ?? undefined : undefined"
        class="mb-4"
        @employment-type="(type: EmploymentTypeEnum) => showAdditionBtn(type, index)"
        @remove="removeEmployment"
        @retired-income="mergeRetiredIncome"
      />
    </template>
    <div v-if="showAnotherButton && !(typeof employmentList[0]?.item === 'string')" class="mt-6">
      <BxButton :id="applicantId + '-income-add-source'" variant="tertiary" @click="addEmployment()">
        <PlusCircleIcon class="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
        Add another income source
      </BxButton>
    </div>
    <AdditionalIncomeForm
      ref="additionalIncomeRef"
      v-model="applicantIncome"
      :applicant-id="applicantId"
      :is-retired="isApplicantRetired"
    />
    <RetirementDetailsForm
      ref="retirementDetailsRef"
      :model-value="props.retirementDetails"
      :applicant-id="applicantId"
      @update:model-value="(v: RetirementDetails) => emit('update:retirementDetails', v)"
    />
    <div v-if="props.showButtons" class="mt-10 flex gap-4">
      <BxButton :id="`${applicantId}-income-save-details`" variant="secondary" @click="save()"
        >Save income details
      </BxButton>
      <BxButton variant="link" @click="cancel()">Cancel</BxButton>
    </div>
  </div>
</template>
